import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { SiteNav } from "../components/SiteNav";
import { Footer } from "../components/sections/Footer";
import { ForgetMeNotIcon } from "../components/ForgetMeNotIcon";

const FADE = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

export default function Research() {
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, []);

    return (
        <div className="min-h-screen bg-[#FDFBF7] text-[#2D2B2A]" data-testid="research-page">
            <SiteNav />

            {/* Hero */}
            <section className="pt-64 pb-20 px-6 rem-petal-bg">
                <div className="max-w-4xl mx-auto">
                    <motion.div {...FADE} className="flex items-center gap-3 mb-6">
                        <ForgetMeNotIcon size={40} />
                        <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#6B7DB8] font-semibold">
                            The research behind remembr
                        </span>
                    </motion.div>
                    <motion.h1
                        {...FADE}
                        transition={{ ...FADE.transition, delay: 0.1 }}
                        className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight"
                        data-testid="research-title"
                    >
                        Reminiscence therapy
                        <br />
                        <em className="italic text-[#4A5A95]">— and why we built this.</em>
                    </motion.h1>
                    <motion.p
                        {...FADE}
                        transition={{ ...FADE.transition, delay: 0.2 }}
                        className="mt-8 text-lg text-[#5C5A59] leading-relaxed max-w-2xl font-light"
                    >
                        remembr is not a wellness app. It is a digital, family-built
                        delivery system for one of the most studied non-drug
                        interventions in modern dementia care — <em>reminiscence
                        therapy</em>. This page sets out the clinical foundation, the
                        evidence, the practice guidelines, and how remembr applies
                        them every day.
                    </motion.p>
                </div>
            </section>

            {/* TL;DR card */}
            <section className="px-6 -mt-6">
                <motion.div
                    {...FADE}
                    className="max-w-4xl mx-auto rounded-3xl bg-white border border-[#E5E1D8] shadow-rem-card p-8 lg:p-12"
                    data-testid="research-tldr"
                >
                    <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#6B7DB8] font-semibold mb-4">
                        In sixty seconds
                    </p>
                    <ul className="space-y-3 text-[#2D2B2A] text-base lg:text-lg leading-relaxed font-light">
                        <li>
                            • Reminiscence therapy (RT) uses prompts from a person's
                            past — photos, voices, music, objects, places — to spark
                            recognition, conversation and connection.
                        </li>
                        <li>
                            • It is recommended by <strong>NICE guideline NG97</strong>
                            (UK) for mild-to-moderate dementia, supported by the
                            largest <strong>Cochrane systematic review</strong> on the
                            topic (Woods et al., 2018) and embedded in the
                            <strong> Alzheimer's Association</strong> dementia-care
                            practice recommendations.
                        </li>
                        <li>
                            • The strongest evidence is for benefits to{" "}
                            <strong>cognition, communication, mood and quality of
                            life</strong> — especially in <strong>care-home
                            settings</strong> (SMD 0.46 for quality of life).
                        </li>
                        <li>
                            • Digital, multimedia delivery (personal photos + music +
                            video) <strong>outperforms generic content</strong> for
                            engagement, mood and social interaction.
                        </li>
                        <li>
                            • The intervention has <strong>no evidence of harm</strong>.
                        </li>
                    </ul>
                </motion.div>
            </section>

            {/* What is RT */}
            <Article id="what-is-rt" eyebrow="Section 01" title="What reminiscence therapy is">
                <p>
                    Reminiscence therapy is a structured, person-centred
                    psychosocial intervention that uses prompts from a person's life
                    history — photographs, music, voice recordings, objects, places,
                    smells — to encourage recall, conversation, and emotional
                    engagement. It is not a memory test. It does not ask the person
                    to remember on command. It places familiar material in front of
                    them and lets recognition do the work.
                </p>
                <p>
                    In mild-to-moderate dementia, long-term autobiographical
                    memory is typically the <em>last</em> capacity to deteriorate.
                    Short-term recall fades first; the wedding day, the work years,
                    the children's births, the lyrics to a song from 1962 — these
                    often remain accessible long after recent events have slipped.
                    Reminiscence therapy operates directly on this preserved
                    territory.
                </p>
                <p>
                    The therapist (or, in our case, the family, or the care staff)
                    is not the protagonist. The <em>life</em> is the protagonist.
                    The intervention's job is to surface it — accurately, warmly,
                    and in the person's own emotional language.
                </p>
            </Article>

            {/* Evidence base */}
            <Article id="evidence" eyebrow="Section 02" title="The evidence base">
                <p>
                    <strong>Cochrane systematic review (Woods et al., 2018).</strong>{" "}
                    The most authoritative synthesis available. Pooled data from{" "}
                    <strong>22 randomised trials, 1,972 participants</strong> with
                    dementia. Findings:
                </p>
                <ul>
                    <li>
                        <strong>Quality of life — care-home settings:</strong>{" "}
                        probable modest improvement immediately after treatment
                        (SMD 0.46; 95% CI 0.18 to 0.75).
                    </li>
                    <li>
                        <strong>Cognition:</strong> high-quality evidence of a small
                        benefit at end of treatment.
                    </li>
                    <li>
                        <strong>Communication and mood:</strong> small positive
                        effects, especially in individual (rather than group)
                        delivery.
                    </li>
                    <li>
                        <strong>Depression — individual reminiscence:</strong>{" "}
                        possible small benefit.
                    </li>
                    <li>
                        <strong>Adverse effects:</strong> none identified.
                    </li>
                </ul>
                <p>
                    <strong>NICE Guideline NG97 (UK, 2018, updated 2023).</strong>{" "}
                    The official UK clinical guideline on dementia
                    assessment, management and support recommends clinicians{" "}
                    <em>consider group reminiscence therapy</em> for people with
                    mild-to-moderate dementia. It sits alongside cognitive
                    stimulation therapy as a frontline non-drug option.
                </p>
                <p>
                    <strong>
                        Alzheimer's Association Dementia Care Practice Recommendations.
                    </strong>{" "}
                    Identifies life-story work and reminiscence as core
                    person-centred practices in long-term-care and residential
                    settings.
                </p>
                <p>
                    <strong>NHS England Palliative Care Guidelines in Dementia
                    (3rd ed., Nov 2024).</strong> Discussing past experiences is
                    described as providing direct <em>cognitive stimulation</em>,
                    particularly because long-term memory is the most preserved
                    cognitive domain in mild-to-moderate dementia.
                </p>
            </Article>

            {/* Why digital */}
            <Article id="digital" eyebrow="Section 03" title="Why digital and multisensory matters">
                <p>
                    The reminiscence-therapy literature is increasingly clear:{" "}
                    <strong>personalised, multimedia delivery outperforms generic
                    materials</strong> for engagement, mood and social interaction.
                </p>
                <ul>
                    <li>
                        A controlled study of digital reminiscence therapy in
                        institutionalised dementia patients found{" "}
                        <strong>significantly lower depression scores and higher
                        engagement</strong> versus standard care (Lazar et al.,
                        replicated in subsequent trials).
                    </li>
                    <li>
                        A 2025 scoping review of immersive (VR-based) reminiscence
                        therapy reported <strong>high engagement, improved mood and
                        social interaction, minimal adverse effects</strong>, and
                        very high acceptance among participants.
                    </li>
                    <li>
                        Systematic reviews of technology-supported RT note that
                        people with dementia show <em>more interest in personalised
                        photo-videos than in standard TV content</em>, and that
                        technology can reduce participation barriers for those with
                        motor impairments — a significant proportion of the
                        population we serve.
                    </li>
                </ul>
                <p>
                    Three mechanisms appear to drive the digital advantage:{" "}
                    <strong>personalisation</strong> (the prompts are the patient's
                    own), <strong>multisensory layering</strong> (photo + voice +
                    music + caption fires more associative pathways than any one
                    alone), and <strong>repetition-with-variation</strong> (the
                    same library, re-sequenced, surfaces different recall paths
                    every viewing).
                </p>
            </Article>

            {/* How remembr applies it */}
            <Article id="how-we-apply" eyebrow="Section 04" title="How remembr applies the research">
                <p>
                    remembr is built around six clinical principles drawn
                    directly from the literature:
                </p>
                <ol>
                    <li>
                        <strong>Personal material first.</strong> Every memory in a
                        remembr session is the patient's own — photographs from
                        the family album, the voices of the people who love them,
                        the music they actually listened to, the streets and
                        houses they actually lived in. Generic stock content is
                        deliberately avoided.
                    </li>
                    <li>
                        <strong>Voice as the primary anchor.</strong> Cloned
                        family voices (with consent) deliver the narration. The
                        evidence on familiar voices in dementia care is consistent:
                        recognition fires faster, and emotional engagement is
                        deeper.
                    </li>
                    <li>
                        <strong>Multisensory layering.</strong> Each chapter
                        combines a still photograph, the era's music or ambient
                        audio, the narration, and a short on-screen caption. Four
                        sensory channels in parallel.
                    </li>
                    <li>
                        <strong>Setting-aware design.</strong> The Cochrane review
                        is clear: effect sizes are largest in care-home settings.
                        remembr is designed to be care-home native — a 20-
                        to 30-minute morning session, headphones-optional, hands-
                        off for the patient, one-touch for the caregiver.
                    </li>
                    <li>
                        <strong>Library, not single playlist (coming).</strong>{" "}
                        As a patient's library grows from a starter set into
                        hundreds of memories accumulated by family contributors,
                        the player will draw fresh sessions every day —{" "}
                        <em>repetition with variation</em>, as the literature
                        recommends. The same memories, re-sequenced, fire different
                        recall pathways each viewing.
                    </li>
                    <li>
                        <strong>Caregiver-led "Lights On" feedback (coming).</strong>{" "}
                        A simple way for a caregiver to mark which memories
                        actually spark recognition for <em>this</em> person. The
                        system learns. Over time, the library weights toward what
                        works.
                    </li>
                </ol>
                <p className="mt-6 text-[#2D2B2A] font-serif italic text-xl text-balance">
                    Reminiscence therapy is the foundation. remembr is the
                    delivery — a next-generation, AI-inspired resource built
                    specifically to take a clinically supported intervention out
                    of the therapist's room and into daily life.
                </p>
            </Article>

            {/* Honest limits */}
            <Article id="limits" eyebrow="Section 05" title="What the evidence does, and does not, claim">
                <p>
                    We want to be careful here. Reminiscence therapy is not a
                    cure for dementia. It does not slow the underlying
                    neuropathology. The effect sizes in the Cochrane review are{" "}
                    <em>small to modest</em>, not transformative. Longer-term
                    follow-up effects are limited, and benefits vary by setting
                    and by how the intervention is delivered.
                </p>
                <p>
                    What the evidence <em>does</em> support is that, when
                    delivered well, reminiscence therapy improves day-to-day{" "}
                    <strong>quality of life</strong>, <strong>cognition</strong>,{" "}
                    <strong>communication</strong> and <strong>mood</strong>, with
                    no identified harm — and that personalised, multimedia
                    delivery substantially improves <strong>engagement</strong>{" "}
                    and the depth of the conversations that follow.
                </p>
                <p>
                    Put plainly: we are not promising to reverse the disease. We
                    are promising — on the basis of the strongest available
                    evidence — to help keep the lights on, for longer, with more
                    warmth, more dignity, and more of the people they love in the
                    room.
                </p>
            </Article>

            {/* Sources */}
            <Article id="sources" eyebrow="Section 06" title="Primary sources">
                <ol className="text-sm leading-relaxed text-[#5C5A59]">
                    <li>
                        Woods B, O'Philbin L, Farrell EM, Spector AE, Orrell M.
                        <em>Reminiscence therapy for dementia.</em> Cochrane
                        Database of Systematic Reviews 2018, Issue 3. Art. No.:
                        CD001120. DOI: 10.1002/14651858.CD001120.pub3.
                    </li>
                    <li>
                        National Institute for Health and Care Excellence (NICE).
                        <em>Dementia: assessment, management and support for
                        people living with dementia and their carers.</em>{" "}
                        Guideline NG97. 2018 (updated 2023).
                    </li>
                    <li>
                        NHS England.{" "}
                        <em>Palliative Care Guidelines in Dementia</em>, 3rd
                        edition. November 2024.
                    </li>
                    <li>
                        Alzheimer's Association.{" "}
                        <em>Dementia Care Practice Recommendations.</em>{" "}
                        2018–present.
                    </li>
                    <li>
                        Lazar A, Thompson H, Demiris G.{" "}
                        <em>A systematic review of the use of technology for
                        reminiscence therapy.</em> Health Education &amp;
                        Behavior, 2014; 41 (1 suppl).
                    </li>
                    <li>
                        Scoping review of VR-based reminiscence therapy for older
                        adults with cognitive impairment. <em>JMIR XR &amp; Spatial
                        Computing</em>, 2025.
                    </li>
                    <li>
                        Systematic review of multimedia-supported reminiscence
                        therapy in dementia care, <em>BMC Geriatrics</em>, 2024.
                    </li>
                </ol>
            </Article>

            {/* CTA back to demo */}
            <section className="px-6 py-24">
                <motion.div
                    {...FADE}
                    className="max-w-3xl mx-auto text-center"
                    data-testid="research-cta"
                >
                    <h2 className="font-serif text-3xl sm:text-4xl text-[#2D2B2A] leading-tight">
                        Now <em className="italic text-[#4A5A95]">watch</em> what
                        this looks like in practice.
                    </h2>
                    <p className="mt-6 text-[#5C5A59] text-lg font-light">
                        John's session is a complete remembr experience,
                        narrated by his family. Six minutes. Headphones if you
                        have them.
                    </p>
                    <Link
                        to="/demo/john-tilly"
                        className="inline-block mt-10 px-8 py-3.5 rounded-full bg-[#6B7DB8] hover:bg-[#4A5A95] text-white text-base font-medium transition-all hover:-translate-y-0.5 shadow-rem-soft"
                        data-testid="research-cta-john"
                    >
                        Watch John's session →
                    </Link>
                </motion.div>
            </section>

            <Footer />
        </div>
    );
}

function Article({ id, eyebrow, title, children }) {
    return (
        <section id={id} className="px-6 py-20 border-t border-[#E5E1D8]/50">
            <div className="max-w-3xl mx-auto">
                <motion.p
                    {...FADE}
                    className="font-sans text-xs uppercase tracking-[0.3em] text-[#6B7DB8] font-semibold mb-4"
                >
                    {eyebrow}
                </motion.p>
                <motion.h2
                    {...FADE}
                    transition={{ ...FADE.transition, delay: 0.05 }}
                    className="font-serif text-3xl sm:text-4xl leading-tight text-[#2D2B2A] mb-8"
                >
                    {title}
                </motion.h2>
                <motion.div
                    {...FADE}
                    transition={{ ...FADE.transition, delay: 0.1 }}
                    className="prose prose-lg max-w-none text-[#5C5A59] font-light leading-relaxed
                        [&>p]:mb-5 [&>ul]:mb-5 [&>ol]:mb-5
                        [&>ul]:list-disc [&>ul]:pl-6 [&>ol]:list-decimal [&>ol]:pl-6
                        [&>ul>li]:mb-2 [&>ol>li]:mb-2
                        [&_strong]:text-[#2D2B2A] [&_strong]:font-medium
                        [&_em]:text-[#4A5A95]"
                >
                    {children}
                </motion.div>
            </div>
        </section>
    );
}
